import {
  Box,
  BoxProps,
  Button as ChakraButton,
  HStack,
  Icon as ChakraIcon,
  IconButton as ChakraIconButton,
  IconButtonProps,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Tooltip,
  useColorModeValue,
  useDisclosure,
  VisuallyHidden,
} from '@chakra-ui/react';
import {
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  Bars3BottomLeftIcon,
  Bars3BottomRightIcon,
  Bars3Icon,
  Bars4Icon,
  ChevronDownIcon,
  CodeBracketIcon,
  LinkIcon,
  MinusIcon,
  PaintBrushIcon,
  PhotoIcon,
  PlusIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';
import {
  $createCodeNode,
  $isCodeNode,
  CODE_LANGUAGE_FRIENDLY_NAME_MAP,
  CODE_LANGUAGE_MAP,
  getDefaultCodeLanguage,
} from '@lexical/code';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import {
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListNode,
} from '@lexical/list';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $createHeadingNode,
  $createQuoteNode,
  $isHeadingNode,
  HeadingTagType,
} from '@lexical/rich-text';
import {
  $getSelectionStyleValueForProperty,
  $isAtNodeEnd,
  $patchStyleText,
  $setBlocksType,
} from '@lexical/selection';
import { $isTableSelection, TableSelection } from '@lexical/table';
import { $findMatchingParent, $getNearestNodeOfType, mergeRegister } from '@lexical/utils';
import { Divider } from '@main/complykit/components/divider';
import { Icon } from '@main/complykit/components/icon';
import { Text } from '@main/complykit/components/text';
import type { ParseKeys } from 'i18next';
import {
  $createParagraphNode,
  $getNodeByKey,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $isTextNode,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_LOW,
  ElementFormatType,
  type ElementNode,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  type LexicalEditor,
  type LexicalNode,
  RangeSelection,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  UNDO_COMMAND,
} from 'lexical';
import {
  type ReactNode,
  type SVGProps,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { BoldIcon } from '../../../icons/bold';
import { ItalicIcon } from '../../../icons/italic';
import { StrikethroughIcon } from '../../../icons/strikethrough';
import { UnderlineIcon } from '../../../icons/underline';
import { useHorizontalScrollShadow } from '../../hooks/horizontal-scroll-shadow';
import { UPLOAD_IMAGE_COMMAND } from './image-upload';
import { InsertTableDialog } from './table';

const FONT_FAMILY_OPTIONS = [
  'Arial',
  'Courier New',
  'Georgia',
  'Times New Roman',
  'Trebuchet MS',
  'Verdana',
] as const;
const FONT_FAMILY_DEFAULT = FONT_FAMILY_OPTIONS[0];

const FONT_SIZE_OPTIONS = [10, 11, 12, 14, 16, 18, 20, 24, 30] as const;
const FONT_SIZE_DEFAULT = '16';
const FONT_SIZE_MIN = FONT_SIZE_OPTIONS[0];
const FONT_SIZE_MAX = 30;
const COMPACT_DROPDOWN_MENU_MAX_HEIGHT = 320;
const COMPACT_DROPDOWN_MENU_GUTTER = 8;

type UiTranslationKey = ParseKeys<'ui'>;

const ELEMENT_FORMAT_OPTIONS = [
  {
    type: 'left',
    labelKey: 'wysiwyg.toolbar.alignOptions.left',
    ariaLabelKey: 'wysiwyg.toolbar.alignOptions.leftAria',
  },
  {
    type: 'center',
    labelKey: 'wysiwyg.toolbar.alignOptions.center',
    ariaLabelKey: 'wysiwyg.toolbar.alignOptions.centerAria',
  },
  {
    type: 'right',
    labelKey: 'wysiwyg.toolbar.alignOptions.right',
    ariaLabelKey: 'wysiwyg.toolbar.alignOptions.rightAria',
  },
  {
    type: 'justify',
    labelKey: 'wysiwyg.toolbar.alignOptions.justify',
    ariaLabelKey: 'wysiwyg.toolbar.alignOptions.justifyAria',
  },
] satisfies {
  type: Exclude<ElementFormatType, ''>;
  labelKey: UiTranslationKey;
  ariaLabelKey: UiTranslationKey;
}[];
const ELEMENT_FORMAT_DEFAULT = 'left';

const BLOCK_TYPES = [
  { value: 'paragraph', labelKey: 'wysiwyg.toolbar.blockTypes.paragraph' },
  { value: 'h1', labelKey: 'wysiwyg.toolbar.blockTypes.heading1' },
  { value: 'h2', labelKey: 'wysiwyg.toolbar.blockTypes.heading2' },
  { value: 'h3', labelKey: 'wysiwyg.toolbar.blockTypes.heading3' },
  { value: 'h4', labelKey: 'wysiwyg.toolbar.blockTypes.heading4' },
  { value: 'h5', labelKey: 'wysiwyg.toolbar.blockTypes.heading5' },
  { value: 'quote', labelKey: 'wysiwyg.toolbar.blockTypes.quote' },
  { value: 'ul', labelKey: 'wysiwyg.toolbar.blockTypes.bulletedList' },
  { value: 'ol', labelKey: 'wysiwyg.toolbar.blockTypes.numberedList' },
  { value: 'code', labelKey: 'wysiwyg.toolbar.blockTypes.codeBlock' },
] as const satisfies readonly { value: string; labelKey: UiTranslationKey }[];
const BLOCK_TYPE_DEFAULT = 'paragraph';

type BlockType = (typeof BLOCK_TYPES)[number]['value'];

const supportedBlockTypes = new Set(BLOCK_TYPES.map((type) => type.value));

function isSupportedBlockTypes(type: string): type is BlockType {
  return supportedBlockTypes.has(type as BlockType);
}

function getSelectedNode(selection: RangeSelection | TableSelection) {
  const anchor = selection.anchor;
  const focus = selection.focus;
  const anchorNode = selection.anchor.getNode();
  const focusNode = selection.focus.getNode();
  if (anchorNode === focusNode) {
    return anchorNode;
  }
  const isBackward = selection.isBackward();
  if (isBackward) {
    return $isAtNodeEnd(focus) ? anchorNode : focusNode;
  } else {
    return $isAtNodeEnd(anchor) ? focusNode : anchorNode;
  }
}

function clearTextFormatting(selection: RangeSelection) {
  if (selection.isCollapsed()) {
    selection.setStyle('');
    for (const format of TEXT_FORMATS) {
      if (selection.hasFormat(format)) {
        selection.toggleFormat(format);
      }
    }
    return;
  }

  for (const node of selection.extract()) {
    if ($isTextNode(node)) {
      node.setFormat(0);
      node.setStyle('');
    }
  }
}

function removeInlineFontSize(style: string) {
  if (!style.toLowerCase().includes('font-size')) {
    return style;
  }

  const declarationsWithoutFontSize = style.split(';').flatMap((declaration) => {
    const trimmedDeclaration = declaration.trim();
    if (trimmedDeclaration === '') {
      return [];
    }

    const colonIndex = trimmedDeclaration.indexOf(':');
    const property = trimmedDeclaration
      .slice(0, colonIndex === -1 ? undefined : colonIndex)
      .trim()
      .toLowerCase();

    return property === 'font-size' ? [] : [trimmedDeclaration];
  });

  return declarationsWithoutFontSize.join('; ');
}

function isSelectedBlock(node: LexicalNode): node is ElementNode {
  return $isElementNode(node) && !node.isInline() && node.getTopLevelElement() !== null;
}

function getSelectedBlock(node: LexicalNode) {
  return isSelectedBlock(node) ? node : $findMatchingParent(node, isSelectedBlock);
}

function clearInlineFontSizeFromSelectedBlocks(selection: RangeSelection) {
  const blocks = new Set(
    [selection.anchor.getNode(), selection.focus.getNode(), ...selection.getNodes()].flatMap(
      (node) => {
        const block = getSelectedBlock(node);

        return block === null ? [] : [block];
      },
    ),
  );

  for (const block of blocks) {
    for (const textNode of block.getAllTextNodes()) {
      const style = textNode.getStyle();
      const nextStyle = removeInlineFontSize(style);
      if (nextStyle === style) {
        continue;
      }

      textNode.setStyle(nextStyle);
    }
  }

  if (selection.isCollapsed()) {
    $patchStyleText(selection, { 'font-size': null });
  }
}

function focusEditorAfterToolbarAction(editor: LexicalEditor) {
  if (typeof window === 'undefined') {
    return;
  }

  window.setTimeout(() => editor.focus(), 0);
}

function getRenderedFontSize(element: HTMLElement | null) {
  if (element === null || typeof window === 'undefined') {
    return FONT_SIZE_DEFAULT;
  }

  return window.getComputedStyle(element).fontSize || FONT_SIZE_DEFAULT;
}

const TEXT_FORMATS = [
  'bold',
  'italic',
  'underline',
  'strikethrough',
  'code',
  'subscript',
  'superscript',
] as const;

export interface ToolbarPluginProps extends BoxProps {
  leftActions?: ReactNode;
  enableImages?: boolean;
  imageUploadAccept?: string;
}

export function ToolbarPlugin(boxProps: ToolbarPluginProps) {
  const [editor] = useLexicalComposerContext();
  const { t } = useTranslation('ui');
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const { enableImages, imageUploadAccept, leftActions, ...restBoxProps } = boxProps;

  const [isEditable, setIsEditable] = useState(() => editor.isEditable());
  const [fontSize, setFontSize] = useState<string>(FONT_SIZE_DEFAULT);
  const [fontFamily, setFontFamily] = useState<string>(FONT_FAMILY_DEFAULT);
  const [elementFormat, setElementFormat] = useState<ElementFormatType>(ELEMENT_FORMAT_DEFAULT);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [blockType, setBlockType] = useState<BlockType>(BLOCK_TYPE_DEFAULT);
  const [selectedElementKey, setSelectedElementKey] = useState<string | null>(null);
  const [codeLanguage, setCodeLanguage] = useState('');
  const [isLink, setIsLink] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isCode, setIsCode] = useState(false);
  const tableModal = useDisclosure();
  const { scrollRef, onScroll, scrollbarStyles, showLeftShadow, showRightShadow } =
    useHorizontalScrollShadow<HTMLDivElement>({
      refreshKey: blockType,
    });

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    let renderedFontSize = FONT_SIZE_DEFAULT;

    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      const element =
        anchorNode.getKey() === 'root' ? anchorNode : anchorNode.getTopLevelElementOrThrow();
      const elementKey = element.getKey();
      const elementDOM = editor.getElementByKey(elementKey);

      if (elementDOM !== null) {
        setSelectedElementKey(elementKey);
        renderedFontSize = getRenderedFontSize(elementDOM);

        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType(anchorNode, ListNode);
          const type = parentList ? parentList.getTag() : element.getTag();
          if (isSupportedBlockTypes(type)) {
            setBlockType(type);
          }
        } else {
          const type = $isHeadingNode(element) ? element.getTag() : element.getType();

          if (isSupportedBlockTypes(type)) {
            setBlockType(type);
          }

          if ($isCodeNode(element)) {
            const language = element.getLanguage() as keyof typeof CODE_LANGUAGE_MAP;
            setCodeLanguage(
              language ? CODE_LANGUAGE_MAP[language] || language : getDefaultCodeLanguage(),
            );
          }
        }
      }

      // Update text format
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));
      setIsCode(selection.hasFormat('code'));
      setFontFamily(
        $getSelectionStyleValueForProperty(selection, 'font-family', FONT_FAMILY_DEFAULT),
      );

      // Update links
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true);
      } else {
        setIsLink(false);
      }

      let matchingParent;
      if ($isLinkNode(parent)) {
        // If node is a link, we need to fetch the parent paragraph node to set format
        matchingParent = $findMatchingParent(
          node,
          (parentNode) => $isElementNode(parentNode) && !parentNode.isInline(),
        );
      }

      // If matchingParent is a valid node, pass it's format type
      setElementFormat(
        $isElementNode(matchingParent)
          ? matchingParent.getFormatType()
          : $isElementNode(node)
            ? node.getFormatType()
            : parent?.getFormatType() || ELEMENT_FORMAT_DEFAULT,
      );
    }

    if ($isRangeSelection(selection) || $isTableSelection(selection)) {
      const nextFontSize = $getSelectionStyleValueForProperty(
        selection,
        'font-size',
        FONT_SIZE_DEFAULT,
      );
      setFontSize(nextFontSize.endsWith('px') ? nextFontSize : renderedFontSize);
    }
  }, [editor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerEditableListener(setIsEditable),
      editor.registerUpdateListener(({ editorState }) => editorState.read(updateToolbar)),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateToolbar();
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        CAN_UNDO_COMMAND,
        (payload) => {
          setCanUndo(payload);
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        CAN_REDO_COMMAND,
        (payload) => {
          setCanRedo(payload);
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    );
  }, [editor, updateToolbar]);

  function onCodeLanguageSelect(lang = getDefaultCodeLanguage()) {
    editor.update(() => {
      if (selectedElementKey !== null) {
        const node = $getNodeByKey(selectedElementKey);
        if ($isCodeNode(node)) {
          node.setLanguage(lang);
        }
      }
    });
  }

  function toggleLink() {
    if (!isLink) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, 'https://');
    } else {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  }

  async function uploadImages(files: FileList | null) {
    const selectedFiles = files ? Array.from(files) : [];
    if (selectedFiles.length > 0) {
      let upload: Promise<void> | undefined;
      editor.dispatchCommand(UPLOAD_IMAGE_COMMAND, {
        files: selectedFiles,
        onComplete: (uploadPromise) => {
          upload = uploadPromise;
        },
      });
      await upload;
    }
  }

  function clearFormatting() {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) {
        return;
      }

      clearTextFormatting(selection);
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    });
    focusEditorAfterToolbarAction(editor);
  }

  const toolbarBackgroundColor = useColorModeValue('#F9F9F9', 'whiteAlpha.100');

  return (
    <Box
      data-policy-editor-toolbar="root"
      position="sticky"
      top={0}
      zIndex={2}
      bg={useColorModeValue('white', 'gray.800')}
      minW="0"
      {...restBoxProps}
    >
      <Box
        data-policy-editor-toolbar="viewport"
        position="relative"
        w="full"
        h="64px"
        overflow="visible"
      >
        <Box
          ref={scrollRef}
          data-policy-editor-toolbar="scroller"
          position="relative"
          overflowX="auto"
          overflowY="hidden"
          sx={scrollbarStyles}
          onScroll={onScroll}
          w="full"
          h="full"
        >
          <Box
            data-policy-editor-toolbar="intrinsic-pill"
            position="relative"
            display="inline-flex"
            minW="calc(100% - 32px)"
            w="max-content"
            h="56px"
            alignItems="center"
            flexShrink={0}
            mx="4"
          >
            <Box
              data-policy-editor-toolbar="background"
              pointerEvents="none"
              position="absolute"
              inset="0"
              bg={toolbarBackgroundColor}
              borderRadius="9999px"
            />
            <HStack
              position="relative"
              zIndex={1}
              w="max-content"
              minW="full"
              alignItems="center"
              h="56px"
              px={4}
              py={0}
              spacing={2}
            >
              {leftActions}
              {leftActions && <ToolbarDivider />}
              <HStack spacing={0}>
                <ToolbarButtonIcon
                  aria-label={t('wysiwyg.toolbar.undo')}
                  icon={<ChakraIcon as={ArrowUturnLeftIcon} />}
                  isDisabled={!canUndo || !isEditable}
                  onClick={() => editor.dispatchCommand(UNDO_COMMAND, undefined)}
                />
                <ToolbarButtonIcon
                  aria-label={t('wysiwyg.toolbar.redo')}
                  icon={<ChakraIcon as={ArrowUturnRightIcon} />}
                  isDisabled={!canRedo || !isEditable}
                  onClick={() => editor.dispatchCommand(REDO_COMMAND, undefined)}
                />
              </HStack>
              <ToolbarDivider />
              <Box flexShrink="0" w="180px">
                <ToolbarBlockFormatDropdown
                  blockType={blockType}
                  onBlockTypeChange={setBlockType}
                />
              </Box>
              <ToolbarDivider />
              {blockType === 'code' ? (
                <Box flexShrink="0" w="168px">
                  <ToolbarCodeLanguageDropdown
                    codeLanguage={codeLanguage}
                    onCodeLanguageChange={onCodeLanguageSelect}
                  />
                </Box>
              ) : (
                <HStack spacing={1} flexShrink="0">
                  <Box w="168px">
                    <ToolbarFontFamilyDropdown
                      fontFamily={fontFamily}
                      onFontFamilyChange={setFontFamily}
                    />
                  </Box>
                  <ToolbarFontSizeDropdown fontSize={fontSize} onFontSizeChange={setFontSize} />
                </HStack>
              )}
              <ToolbarDivider />
              <HStack spacing={0}>
                <ToolbarButtonIcon
                  aria-label={t('wysiwyg.toolbar.bold')}
                  isActive={isBold}
                  isDisabled={!isEditable}
                  icon={<ChakraIcon as={BoldIcon} />}
                  onClick={() => {
                    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
                    focusEditorAfterToolbarAction(editor);
                  }}
                />
                <ToolbarButtonIcon
                  aria-label={t('wysiwyg.toolbar.italic')}
                  isActive={isItalic}
                  isDisabled={!isEditable}
                  icon={<ChakraIcon as={ItalicIcon} />}
                  onClick={() => {
                    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
                    focusEditorAfterToolbarAction(editor);
                  }}
                />
                <ToolbarButtonIcon
                  aria-label={t('wysiwyg.toolbar.underline')}
                  isActive={isUnderline}
                  isDisabled={!isEditable}
                  icon={<ChakraIcon as={UnderlineIcon} />}
                  onClick={() => {
                    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
                    focusEditorAfterToolbarAction(editor);
                  }}
                />
                <ToolbarButtonIcon
                  aria-label={t('wysiwyg.toolbar.strikethrough')}
                  isActive={isStrikethrough}
                  isDisabled={!isEditable}
                  icon={<ChakraIcon as={StrikethroughIcon} />}
                  onClick={() => {
                    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
                    focusEditorAfterToolbarAction(editor);
                  }}
                />
                <ToolbarButtonIcon
                  aria-label={t('wysiwyg.toolbar.insertCode')}
                  isActive={isCode}
                  isDisabled={!isEditable}
                  icon={<ChakraIcon as={CodeBracketIcon} />}
                  onClick={() => {
                    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'code');
                    focusEditorAfterToolbarAction(editor);
                  }}
                />
                <ToolbarButtonIcon
                  aria-label={t('wysiwyg.toolbar.insertLink')}
                  isActive={isLink}
                  isDisabled={!isEditable}
                  icon={<ChakraIcon as={LinkIcon} />}
                  onClick={toggleLink}
                />
                <ToolbarButtonIcon
                  aria-label={t('wysiwyg.toolbar.insertTable')}
                  isDisabled={!isEditable}
                  icon={<ChakraIcon as={TableCellsIcon} />}
                  onClick={tableModal.onOpen}
                />
                {enableImages ? (
                  <>
                    <input
                      ref={imageInputRef}
                      type="file"
                      accept={imageUploadAccept}
                      multiple
                      hidden
                      onChange={(event) => {
                        const input = event.currentTarget;
                        void uploadImages(input.files).finally(() => {
                          input.value = '';
                        });
                      }}
                    />
                    <ToolbarButtonIcon
                      aria-label={t('wysiwyg.toolbar.insertImage')}
                      isDisabled={!isEditable}
                      icon={<ChakraIcon as={PhotoIcon} />}
                      onClick={() => imageInputRef.current?.click()}
                    />
                  </>
                ) : null}
                <InsertTableDialog {...tableModal} />
              </HStack>
              <ToolbarDivider />
              <Box flexShrink="0">
                <ToolbarElementFormatControls
                  elementFormat={elementFormat}
                  onElementFormatChange={setElementFormat}
                />
              </Box>
              <ToolbarButtonIcon
                aria-label={t('wysiwyg.toolbar.clearFormatting')}
                isDisabled={!isEditable}
                icon={<ChakraIcon as={PaintBrushIcon} />}
                onClick={clearFormatting}
              />
            </HStack>
          </Box>
        </Box>
        {showLeftShadow && <ToolbarScrollShadow side="left" />}
        {showRightShadow && <ToolbarScrollShadow side="right" />}
      </Box>
    </Box>
  );
}

function ToolbarScrollShadow({ side }: { side: 'left' | 'right' }) {
  const gradient = useColorModeValue(
    side === 'left'
      ? 'linear(to-r, rgba(216, 215, 215, 0.8) 0%, rgba(216, 215, 215, 0.3) 32.5%, rgba(255, 255, 255, 0) 72.5%)'
      : 'linear(to-l, rgba(216, 215, 215, 0.8) 0%, rgba(216, 215, 215, 0.3) 32.5%, rgba(255, 255, 255, 0) 72.5%)',
    side === 'left'
      ? 'linear(to-r, rgba(0, 0, 0, 0.14), transparent)'
      : 'linear(to-l, rgba(0, 0, 0, 0.14), transparent)',
  );

  return (
    <Box
      data-policy-editor-toolbar-shadow={side}
      position="absolute"
      top={0}
      bottom={0}
      {...(side === 'left' ? { left: 0 } : { right: 0 })}
      w="38px"
      h="56px"
      pointerEvents="none"
      bgGradient={gradient}
      borderRadius={0}
      zIndex={2}
    />
  );
}

interface ToolbarButtonIconProps extends IconButtonProps {
  isActive?: boolean;
}

function ToolbarDivider() {
  return (
    <Box h="30px" alignSelf="center" display="flex">
      <Divider orientation="vertical" />
    </Box>
  );
}

/* Stays Chakra for now: the isActive toggle state and the toolbar geometry
 * have no ComplyKit counterpart yet (pressed state is React Aria's, never a
 * prop), and the surrounding dropdowns are MenuButton triggers. */
/* Parked with the editor toolbar round: a formatting toggle must keep focus
 * in the editor when pressed, and ComplyKit's press model has no such option
 * yet (component-map.md). */
function ToolbarButtonIcon(props: ToolbarButtonIconProps) {
  const label = props.title ?? props['aria-label'];
  const colorLight = props.isActive ? 'blue.700' : '#646464';
  const colorDark = props.isActive ? 'blue.100' : 'gray.400';
  return (
    <Tooltip isDisabled={!label} label={label} hasArrow>
      <ChakraIconButton
        size="sm"
        variant={props.isActive ? 'solid' : 'ghost'}
        color={useColorModeValue(colorLight, colorDark)}
        borderRadius="6px"
        px={2}
        {...props}
        aria-label={props['aria-label'] || props.title || ''}
      />
    </Tooltip>
  );
}

interface ToolbarBlockFormatDropdownProps {
  blockType: BlockType;
  onBlockTypeChange(type: BlockType): void;
}

function ToolbarBlockFormatDropdown({
  blockType,
  onBlockTypeChange,
}: ToolbarBlockFormatDropdownProps) {
  const [editor] = useLexicalComposerContext();
  const { t } = useTranslation('ui');

  const options = useMemo(
    () => BLOCK_TYPES.map((type) => ({ value: type.value, label: t(type.labelKey) })),
    [t],
  );

  const selectedBlockType = useMemo(
    () => options.find((type) => type.value === blockType),
    [blockType, options],
  );

  function formatParagraph() {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createParagraphNode());
      }
    });
  }

  function formatHeading(headingSize: HeadingTagType) {
    if (blockType !== headingSize) {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          clearInlineFontSizeFromSelectedBlocks(selection);
        }
        $setBlocksType(selection, () => $createHeadingNode(headingSize));
      });
    }
  }

  function formatBulletList() {
    if (blockType !== 'ul') {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    } else {
      formatParagraph();
    }
  }

  function formatNumberedList() {
    if (blockType !== 'ol') {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    } else {
      formatParagraph();
    }
  }

  function formatQuote() {
    editor.update(() => {
      const selection = $getSelection();
      $setBlocksType(selection, () => $createQuoteNode());
    });
  }

  function formatCode() {
    editor.update(() => {
      let selection = $getSelection();

      if (selection !== null) {
        if (selection.isCollapsed()) {
          $setBlocksType(selection, () => $createCodeNode());
        } else {
          const textContent = selection.getTextContent();
          const codeNode = $createCodeNode();
          selection.insertNodes([codeNode]);
          selection = $getSelection();
          if ($isRangeSelection(selection)) {
            selection.insertRawText(textContent);
          }
        }
      }
    });
  }

  function handleBlockTypeChange(type: BlockType) {
    switch (type) {
      case 'paragraph':
        formatParagraph();
        break;
      case 'h1':
      case 'h2':
      case 'h3':
      case 'h4':
      case 'h5':
        formatHeading(type);
        break;
      case 'ul':
        formatBulletList();
        break;
      case 'ol':
        formatNumberedList();
        break;
      case 'quote':
        formatQuote();
        break;
      case 'code':
        formatCode();
        break;
    }
    onBlockTypeChange(type);
    focusEditorAfterToolbarAction(editor);
  }

  return (
    <ToolbarCompactDropdown
      isDisabled={!editor.isEditable()}
      ariaLabel={t('wysiwyg.toolbar.blockType')}
      options={options}
      value={selectedBlockType?.value ?? BLOCK_TYPE_DEFAULT}
      triggerLabel={selectedBlockType?.label ?? options[0]?.label ?? ''}
      onChange={handleBlockTypeChange}
      minMenuWidth="180px"
    />
  );
}

interface ToolbarCompactDropdownProps<TValue extends string> {
  isDisabled?: boolean;
  ariaLabel: string;
  options: readonly { value: TValue; label: string }[];
  value: TValue;
  triggerLabel: string;
  onChange(value: TValue): void;
  minMenuWidth?: string;
}

function ToolbarCompactDropdown<TValue extends string>({
  isDisabled,
  ariaLabel,
  options,
  value,
  triggerLabel,
  onChange,
  minMenuWidth = '160px',
}: ToolbarCompactDropdownProps<TValue>) {
  const { isOpen, onClose, onOpen } = useDisclosure();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [menuPlacement, setMenuPlacement] = useState<'bottom-start' | 'top-start'>('bottom-start');
  const [menuMaxHeight, setMenuMaxHeight] = useState(`${COMPACT_DROPDOWN_MENU_MAX_HEIGHT}px`);
  const triggerColor = useColorModeValue('#646464', 'gray.300');
  const menuBorderColor = useColorModeValue('rgba(0, 0, 0, 0.06)', 'whiteAlpha.300');
  const menuShadow = useColorModeValue(
    '0px 8px 24px rgba(15, 23, 42, 0.08)',
    '0px 8px 24px rgba(0, 0, 0, 0.45)',
  );
  const menuItemColor = useColorModeValue('#646464', 'gray.200');
  const selectedItemBg = useColorModeValue('blackAlpha.50', 'whiteAlpha.200');

  const updateMenuGeometry = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const trigger = triggerRef.current;
    if (!trigger) {
      return;
    }

    const { top, bottom } = trigger.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const availableAbove = Math.max(0, top - COMPACT_DROPDOWN_MENU_GUTTER);
    const availableBelow = Math.max(0, viewportHeight - bottom - COMPACT_DROPDOWN_MENU_GUTTER);
    const nextPlacement = availableBelow >= availableAbove ? 'bottom-start' : 'top-start';
    const availableHeight = nextPlacement === 'bottom-start' ? availableBelow : availableAbove;
    const nextMaxHeight = Math.max(
      1,
      Math.min(COMPACT_DROPDOWN_MENU_MAX_HEIGHT, Math.floor(availableHeight)),
    );

    setMenuPlacement(nextPlacement);
    setMenuMaxHeight(`${nextMaxHeight}px`);
  }, []);

  const handleOpen = useCallback(() => {
    updateMenuGeometry();
    onOpen();
  }, [onOpen, updateMenuGeometry]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    updateMenuGeometry();
    window.addEventListener('resize', updateMenuGeometry);
    window.addEventListener('scroll', updateMenuGeometry, true);

    return () => {
      window.removeEventListener('resize', updateMenuGeometry);
      window.removeEventListener('scroll', updateMenuGeometry, true);
    };
  }, [isOpen, updateMenuGeometry]);

  return (
    <Menu
      isOpen={isOpen}
      onClose={onClose}
      onOpen={handleOpen}
      placement={menuPlacement}
      strategy="fixed"
      gutter={COMPACT_DROPDOWN_MENU_GUTTER}
      preventOverflow
      flip
    >
      <Tooltip label={ariaLabel} hasArrow>
        <MenuButton
          ref={triggerRef}
          as={ChakraButton}
          size="sm"
          variant="ghost"
          h="32px"
          w="full"
          px={2}
          borderRadius="6px"
          minW="0"
          isDisabled={isDisabled}
          color={triggerColor}
          fontFamily="Inter"
          fontSize="sm"
          fontWeight="500"
          aria-label={ariaLabel}
          data-policy-editor-toolbar-trigger={ariaLabel}
        >
          <HStack as="span" w="full" minW="0" spacing={2} justifyContent="space-between">
            <Text as="span" size="sm" isTruncated isGrow>
              {triggerLabel}
            </Text>
            <Icon as={ChevronDownIcon} />
          </HStack>
        </MenuButton>
      </Tooltip>
      <Portal>
        <MenuList
          data-policy-editor-toolbar-menu={ariaLabel}
          data-policy-editor-toolbar-menu-placement={menuPlacement}
          data-policy-editor-toolbar-menu-max-height={menuMaxHeight}
          minW={minMenuWidth}
          w="max-content"
          maxH={menuMaxHeight}
          overflowY="auto"
          py={1}
          borderRadius="8px"
          borderColor={menuBorderColor}
          boxShadow={menuShadow}
          zIndex="popover"
        >
          {options.map((option) => (
            <MenuItem
              key={option.value}
              h="32px"
              px={3}
              fontSize="sm"
              fontWeight="500"
              color={menuItemColor}
              bg={option.value === value ? selectedItemBg : undefined}
              onClick={() => onChange(option.value)}
            >
              {option.label}
            </MenuItem>
          ))}
        </MenuList>
      </Portal>
    </Menu>
  );
}

interface ToolbarFontFamilyDropdownProps {
  fontFamily: string;
  onFontFamilyChange(fontFamily: string): void;
}

function ToolbarFontFamilyDropdown({
  fontFamily,
  onFontFamilyChange,
}: ToolbarFontFamilyDropdownProps) {
  const [editor] = useLexicalComposerContext();
  const { t } = useTranslation('ui');

  const options = useMemo(
    () => FONT_FAMILY_OPTIONS.map((font) => ({ value: font, label: font })),
    [],
  );
  const selectedOption = useMemo(
    () => options.find((option) => option.value === fontFamily),
    [fontFamily, options],
  );

  function handleChange(nextFontFamily: string) {
    editor.update(() => {
      const selection = $getSelection();
      if (selection !== null) {
        $patchStyleText(selection, { 'font-family': nextFontFamily });
      }
      onFontFamilyChange(nextFontFamily);
    });
    focusEditorAfterToolbarAction(editor);
  }

  return (
    <ToolbarCompactDropdown
      isDisabled={!editor.isEditable()}
      ariaLabel={t('wysiwyg.toolbar.fontFamily')}
      options={options}
      value={selectedOption?.value ?? FONT_FAMILY_DEFAULT}
      triggerLabel={selectedOption?.label ?? FONT_FAMILY_DEFAULT}
      onChange={handleChange}
      minMenuWidth="190px"
    />
  );
}

interface ToolbarFontSizeDropdownProps {
  fontSize: string;
  onFontSizeChange(fontFamily: string): void;
}

function ToolbarFontSizeDropdown({ fontSize, onFontSizeChange }: ToolbarFontSizeDropdownProps) {
  const [editor] = useLexicalComposerContext();
  const { t } = useTranslation('ui');
  const numericFontSize = normalizeFontSize(fontSize);

  function handleChange(nextFontSize: string) {
    editor.update(() => {
      const selection = $getSelection();
      if (selection !== null) {
        $patchStyleText(selection, { 'font-size': nextFontSize });
      }
      onFontSizeChange(nextFontSize);
    });
    focusEditorAfterToolbarAction(editor);
  }

  const decreaseFontSize = () => handleChange(`${getNextFontSize(numericFontSize, -1)}px`);
  const increaseFontSize = () => handleChange(`${getNextFontSize(numericFontSize, 1)}px`);

  return (
    <HStack spacing={0} role="group" aria-label={t('wysiwyg.toolbar.fontSize')} flexShrink="0">
      <ToolbarButtonIcon
        aria-label={t('wysiwyg.toolbar.decreaseFontSize')}
        icon={<ChakraIcon as={MinusIcon} />}
        isDisabled={!editor.isEditable() || numericFontSize <= FONT_SIZE_MIN}
        minW="28px"
        onClick={decreaseFontSize}
        px={0}
        w="28px"
      />
      <ChakraButton
        size="sm"
        variant="outline"
        color={useColorModeValue('#646464', 'gray.400')}
        borderColor={useColorModeValue('rgba(0, 0, 0, 0.06)', 'whiteAlpha.300')}
        borderRadius="6px"
        h="32px"
        minW="34px"
        px={1}
        cursor="default"
        tabIndex={-1}
        aria-label={t('wysiwyg.toolbar.fontSizeValue', { size: numericFontSize })}
      >
        {numericFontSize}
      </ChakraButton>
      <ToolbarButtonIcon
        aria-label={t('wysiwyg.toolbar.increaseFontSize')}
        icon={<ChakraIcon as={PlusIcon} />}
        isDisabled={!editor.isEditable() || numericFontSize >= FONT_SIZE_MAX}
        minW="28px"
        onClick={increaseFontSize}
        px={0}
        w="28px"
      />
    </HStack>
  );
}

function normalizeFontSize(fontSize: string) {
  const parsed = Number.parseInt(fontSize, 10);
  return Number.isFinite(parsed) ? parsed : Number.parseInt(FONT_SIZE_DEFAULT, 10);
}

function getNextFontSize(currentFontSize: number, direction: -1 | 1) {
  if (direction === 1) {
    return FONT_SIZE_OPTIONS.find((size) => size > currentFontSize) ?? FONT_SIZE_MAX;
  }

  return [...FONT_SIZE_OPTIONS].reverse().find((size) => size < currentFontSize) ?? FONT_SIZE_MIN;
}

type HeroIcon = (
  props: SVGProps<SVGSVGElement> & { title?: string; titleId?: string },
) => JSX.Element;

const ELEMENT_FORMAT_ICON_MAP: Partial<Record<ElementFormatType, HeroIcon>> = {
  center: Bars3Icon,
  justify: Bars4Icon,
  left: Bars3BottomLeftIcon,
  right: Bars3BottomRightIcon,
  start: Bars3BottomLeftIcon,
  end: Bars3BottomRightIcon,
};

function ToolbarElementFormatControls({
  elementFormat,
  onElementFormatChange,
}: {
  elementFormat: ElementFormatType;
  onElementFormatChange(format: ElementFormatType): void;
}) {
  const [editor] = useLexicalComposerContext();
  const { t } = useTranslation('ui');
  const options = useMemo(
    () =>
      ELEMENT_FORMAT_OPTIONS.filter((format) => format.type in ELEMENT_FORMAT_ICON_MAP).map(
        (format) => ({
          type: format.type,
          label: t(format.labelKey),
          ariaLabel: t(format.ariaLabelKey),
        }),
      ),
    [t],
  );
  const menuBorderColor = useColorModeValue('rgba(0, 0, 0, 0.06)', 'whiteAlpha.300');
  const selectedItemBg = useColorModeValue('blackAlpha.50', 'whiteAlpha.200');
  const hoveredItemBg = useColorModeValue('blackAlpha.100', 'whiteAlpha.300');

  function handleChange(format: ElementFormatType) {
    editor.update(() => {
      editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, format);
      onElementFormatChange(format);
    });
    focusEditorAfterToolbarAction(editor);
  }

  const activeFormat = options.find((o) => o.type === elementFormat) || options[0];
  const ActiveIcon =
    ELEMENT_FORMAT_ICON_MAP[elementFormat] ??
    (activeFormat ? ELEMENT_FORMAT_ICON_MAP[activeFormat.type] : Bars3BottomLeftIcon) ??
    Bars3BottomLeftIcon;

  return (
    <Menu
      placement="bottom-end"
      strategy="fixed"
      gutter={COMPACT_DROPDOWN_MENU_GUTTER}
      preventOverflow
      flip
      computePositionOnMount
      modifiers={[
        {
          name: 'preventOverflow',
          options: {
            altAxis: true,
            mainAxis: true,
            padding: COMPACT_DROPDOWN_MENU_GUTTER,
            rootBoundary: 'viewport',
          },
        },
      ]}
    >
      <Tooltip label={t('wysiwyg.toolbar.align')} hasArrow>
        <MenuButton
          as={ChakraButton}
          size="sm"
          variant="ghost"
          color={useColorModeValue('#646464', 'gray.400')}
          borderRadius="6px"
          isDisabled={!editor.isEditable()}
          minW="40px"
          w="40px"
          h="32px"
          px={0}
          aria-label={t('wysiwyg.toolbar.align')}
          data-policy-editor-toolbar-trigger="Align"
        >
          <HStack spacing={0.5} alignItems="center" justifyContent="center">
            <Icon as={ActiveIcon} />
            <Icon as={ChevronDownIcon} size="sm" />
          </HStack>
        </MenuButton>
      </Tooltip>
      <Portal>
        <MenuList
          data-policy-editor-toolbar-menu="Align"
          minW="0"
          w="128px"
          maxW="calc(100vw - 16px)"
          display="flex"
          gap={0}
          p={0}
          borderRadius="6px"
          borderColor={menuBorderColor}
          overflow="hidden"
          zIndex="popover"
        >
          {options.map((format, index) => {
            const FormatIcon = ELEMENT_FORMAT_ICON_MAP[format.type] ?? Bars3BottomLeftIcon;
            const isSelected = format.type === elementFormat;
            return (
              <MenuItem
                key={format.type}
                aria-label={format.ariaLabel}
                title={format.ariaLabel}
                onClick={() => handleChange(format.type)}
                minW="32px"
                w="32px"
                h="32px"
                px={0}
                justifyContent="center"
                bg={isSelected ? selectedItemBg : undefined}
                borderRight={index === options.length - 1 ? '0' : '1px'}
                borderColor={menuBorderColor}
                _hover={{ bg: hoveredItemBg }}
                _focus={{ bg: hoveredItemBg }}
              >
                <Tooltip label={format.ariaLabel} hasArrow>
                  <Box as="span" display="inline-flex">
                    <Icon as={FormatIcon} />
                  </Box>
                </Tooltip>
                <VisuallyHidden>{format.ariaLabel}</VisuallyHidden>
              </MenuItem>
            );
          })}
        </MenuList>
      </Portal>
    </Menu>
  );
}

interface ToolbarCodeLanguageDropdownProps {
  codeLanguage: string;
  onCodeLanguageChange(language: string): void;
}

function ToolbarCodeLanguageDropdown({
  codeLanguage,
  onCodeLanguageChange,
}: ToolbarCodeLanguageDropdownProps) {
  const [editor] = useLexicalComposerContext();
  const { t } = useTranslation('ui');

  const options = useMemo(
    () =>
      Object.entries(CODE_LANGUAGE_FRIENDLY_NAME_MAP).map(([lang, name]) => ({
        value: lang,
        label: name,
      })),
    [],
  );
  const selectedOption = useMemo(
    () => options.find((option) => option.value === codeLanguage),
    [codeLanguage, options],
  );
  const fallbackLanguage = getDefaultCodeLanguage();
  const fallbackLabel = CODE_LANGUAGE_FRIENDLY_NAME_MAP[fallbackLanguage] ?? fallbackLanguage;

  return (
    <ToolbarCompactDropdown
      isDisabled={!editor.isEditable()}
      options={options}
      ariaLabel={t('wysiwyg.toolbar.codeLanguage')}
      value={selectedOption?.value ?? fallbackLanguage}
      triggerLabel={selectedOption?.label ?? fallbackLabel}
      onChange={onCodeLanguageChange}
      minMenuWidth="190px"
    />
  );
}
